// src/components/audition-booking-wizard/Step1_SelectMode.jsx
import React from 'react';
import { Button, Row, Col, Alert } from 'react-bootstrap';
import { 
    FaCalendarCheck, // For Book Slot (F2F/Walk-in)
    FaBookOpen,      // For Book (Online)
    FaClipboardCheck, // For Prepare (All)
    FaMicrophone,    // For Record (Online)
    FaUpload,        // For Upload (Online)
    FaUserTie,       // For Interview (F2F/Walk-in)
    FaCheckCircle,   // For Result (F2F/Walk-in)
    // Removed unused: FaFileAlt, FaVideo, FaCloudUploadAlt, FaClock, FaMapMarkerAlt, FaTicketAlt, FaUsers, FaHandshake
} from 'react-icons/fa'; 
// Removed unused image imports (bookImg, prepareImg, recordImg, uploadImg) as FontAwesome icons are used

import DateStrip from './scheduler/DateStrip';
import TimeGrid from './scheduler/TimeGrid';

const Step1_SelectMode = ({
    audition, selectedMode, handleSelectMode, handleNext,
    showOnline, showF2F, showWalkin,
    selectedDate, setSelectedDate, selectedTimeSlot, setSelectedTimeSlot, setSelectedSlotId,
    availableSlots, loadingSlots, slotError,
    scrollRef, handleScroll
}) => {
    // Helper to filter slots based on selectedDate
    const getFilteredSlots = (slots, date) => {
        return slots.filter(s => s.dateOnly === date);
    };

    // Extract unique dates for the date strip
    const uniqueDates = [...new Set(availableSlots.map(s => s.dateOnly))];
    
    // Auto-select first date if none selected yet
    React.useEffect(() => {
        if (!selectedDate && uniqueDates.length > 0) {
            setSelectedDate(uniqueDates[0]);
        }
    }, [selectedDate, uniqueDates, setSelectedDate]);

    return (
        <div className="options-container animate-fade-in">
            <Row className="g-4">
                {showOnline && (
                    <Col md={6}>
                        <div className={`option-card ${selectedMode === 'ONLINE' ? 'selected' : ''}`} onClick={() => { handleSelectMode('ONLINE'); setSelectedTimeSlot('Immediate') }}>
                            <div className="option-text-section">
                                <span className="option-title">ONLINE AUDITION</span>
                                <p className="option-desc">Book, prepare, record & upload.</p>
                            </div>
                            <div className="option-icons-horizontal">
                                <div className="icon-item">
                                    <div className="icon-box-glow"><FaBookOpen size={22} /></div>
                                    <span className="icon-label">Book</span>
                                </div>
                                <div className="icon-item">
                                    <div className="icon-box-glow"><FaClipboardCheck size={22} /></div>
                                    <span className="icon-label">Prepare</span>
                                </div>
                                <div className="icon-item">
                                    <div className="icon-box-glow"><FaMicrophone size={22} /></div>
                                    <span className="icon-label">Record</span>
                                </div>
                                <div className="icon-item">
                                    <div className="icon-box-glow"><FaUpload size={22} /></div>
                                    <span className="icon-label">Upload</span>
                                </div>
                            </div>
                        </div>
                    </Col>
                )}
                 {showF2F && ( // Group F2F and Walkin as they share icons
                    <Col md={6}>
                        <div className={`option-card ${selectedMode === 'F2F' ? 'selected' : ''}`} onClick={() => handleSelectMode('F2F')}>
                            <div className="option-text-section">
                                <span className="option-title">FACE-TO-FACE AUDITION</span>
                                <p className="option-desc">Book Slot, Prepare, Interview, Result</p> {/* Updated desc */}
                            </div>
                            <div className="option-icons-horizontal">
                                <div className="icon-item">
                                    <div className="icon-box-glow"><FaCalendarCheck size={22} /></div>
                                    <span className="icon-label">Book Slot</span> {/* Updated label */}
                                </div>
                                <div className="icon-item">
                                    <div className="icon-box-glow"><FaClipboardCheck size={22} /></div> {/* Changed icon */}
                                    <span className="icon-label">Prepare</span> {/* Updated label */}
                                </div>
                                <div className="icon-item">
                                    <div className="icon-box-glow"><FaUserTie size={22} /></div>
                                    <span className="icon-label">Interview</span> {/* Updated label */}
                                </div>
                                <div className="icon-item">
                                    <div className="icon-box-glow"><FaCheckCircle size={22} /></div> {/* Changed icon */}
                                    <span className="icon-label">Result</span> {/* Updated label */}
                                </div>
                            </div>
                        </div>
                    </Col>
                )}
                {/* Walk-in will use the same card as F2F if desired, or you can separate it */}
                {/* For now, I've grouped F2F and Walkin to share icons as per your description */}
                {/* If you want a separate card for Walk-in, simply uncomment and adjust below: */}
                {showWalkin && (
                    <Col md={6}>
                        <div className={`option-card ${selectedMode === 'WALKIN' ? 'selected' : ''}`} onClick={() => handleSelectMode('WALKIN')}>
                            <div className="option-text-section">
                                <span className="option-title">WALK-IN AUDITION</span>
                                <p className="option-desc">Book Slot, Prepare, Interview, Result</p>
                            </div>
                            <div className="option-icons-horizontal">
                                <div className="icon-item">
                                    <div className="icon-box-glow"><FaCalendarCheck size={22} /></div>
                                    <span className="icon-label">Book Slot</span> {/* Updated label */}
                                </div>
                                <div className="icon-item">
                                    <div className="icon-box-glow"><FaClipboardCheck size={22} /></div> {/* Changed icon */}
                                    <span className="icon-label">Prepare</span> {/* Updated label */}
                                </div>
                                <div className="icon-item">
                                    <div className="icon-box-glow"><FaUserTie size={22} /></div>
                                    <span className="icon-label">Interview</span> {/* Updated label */}
                                </div>
                                <div className="icon-item">
                                    <div className="icon-box-glow"><FaCheckCircle size={22} /></div> {/* Changed icon */}
                                    <span className="icon-label">Result</span> {/* Updated label */}
                                </div>
                            </div>
                        </div>
                    </Col>
                )}

                {(selectedMode === 'F2F' || selectedMode === 'WALKIN') && (
                    <Col xs={12} className="mt-4">
                        <h6 className="text-white mb-3">Select Date & Time for {selectedMode === 'F2F' ? 'Face-to-Face' : 'Walk-In'} Audition</h6>
                        <div className="f2f-scheduler-wrapper mb-4 animate-fade-in" style={{ background: "rgba(255,255,255,0.03)", padding: "15px", borderRadius: "12px" }}>
                            {slotError && <Alert variant="danger">{slotError}</Alert>}
                            <DateStrip 
                                uniqueDates={uniqueDates} 
                                selectedDate={selectedDate} 
                                setSelectedDate={setSelectedDate}
                                scrollRef={scrollRef}
                                handleScroll={handleScroll}
                            />
                            <TimeGrid 
                                loadingSlots={loadingSlots} 
                                filteredSlots={getFilteredSlots(availableSlots, selectedDate)} 
                                selectedTimeSlot={selectedTimeSlot} 
                                setSelectedTimeSlot={setSelectedTimeSlot}
                                setSelectedSlotId={setSelectedSlotId}
                            />
                        </div>
                    </Col>
                )}
            </Row>
            <div className="text-end mt-3"><Button className="btn-next-white" onClick={handleNext}>NEXT</Button></div>
        </div>
    );
};

export default Step1_SelectMode;